import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@staff-app/ui';
import { RefreshCw, Search } from 'lucide-react';
import { DEMO_PAGE_SIZE, useDemoCoupons, useSyncDemoCouponUsage } from '../hooks/useDemoCoupons';
import { DemoCouponForm } from '../components/DemoCouponForm';
import { DemoCouponTable } from '../components/DemoCouponTable';
import { MintedCouponDialog, type MintedCoupon } from '../components/MintedCouponDialog';

const ALL = '__all__';
const STATUSES = ['ACTIVE', 'USED', 'EXPIRED'];

/**
 * Demo Coupons — the first section of Devices Fulfillment (DevicesFulfillmentShell owns the h1).
 *
 * It leads with the CARD and puts the ledger under it, the opposite of the Devices Order queue:
 * that page fulfils orders someone already paid for, so it leads with the work list. Here the act
 * comes first and the record follows — the ledger answers what happened next: what went out, to
 * whom, and whether it was ever redeemed.
 */
export function DemoCouponsPage() {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  // Owned by the PAGE, like the invoice queue's: a successful create re-renders the list beneath
  // it, and a dialog held by the form would be at the mercy of that.
  const [minted, setMinted] = useState<MintedCoupon | null>(null);

  useEffect(() => {
    setPageIndex(0);
  }, [search, status]);

  const { data, loading, error, refetch } = useDemoCoupons(
    search,
    status,
    pageIndex * DEMO_PAGE_SIZE,
  );
  const page = data?.demoCoupons;
  const items = page?.items ?? [];
  const total = page?.total ?? 0;
  const firstShown = total === 0 ? 0 : pageIndex * DEMO_PAGE_SIZE + 1;
  const lastShown = Math.min(total, pageIndex * DEMO_PAGE_SIZE + items.length);

  const [syncUsage, { loading: syncing }] = useSyncDemoCouponUsage();
  const refreshUsage = async () => {
    // Scoped to the codes on screen; the 15-minute background sweep does the rest.
    await syncUsage({ variables: { codes: items.map((c) => c.code) } });
    await refetch();
  };

  // THE PAGE SCROLLS, unlike its neighbours.
  //
  // Devices Order pins itself to the viewport (`h-full overflow-hidden`) and scrolls its table
  // inside — right for a page that is one filter row above one work list. This page stacks a tall
  // create card ON TOP of a ledger, and squeezing both into the viewport clipped the card on a
  // laptop with no way to reach what was cut off. So it grows to its content and the shell's
  // outlet container (`min-h-0 flex-1 overflow-auto`) does the scrolling, which keeps the h1 and
  // the section tabs pinned while the content moves under them.
  return (
    <div className="flex flex-col gap-6 p-6" data-testid="demo-coupons-page">
      {/* No heading: the shell owns the page title, and the active tab already names this
          section. Only what is specific to demo coupons earns space here. */}
      <p className="text-sm text-muted-foreground">
        {'Coupon codes for DEMO devices. The customer redeems the code in the portal’s Order '}
        {'Devices checkout. Every code is single-use, covers one device type, ships US only, and '}
        {'expires 30 days after it is issued.'}
      </p>

      <DemoCouponForm
        onCreated={(c) =>
          setMinted({
            code: c.code,
            amount: c.amount,
            deviceName: c.deviceName,
            quantity: c.quantity,
            // Now that the card collects it, the confirmation can name who the code is for —
            // which is the sentence someone reads before pasting it into an email.
            customer: c.customerEmail,
          })
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[280px] flex-1 max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search code, device, customer, reason, who issued it…"
            className="pl-9"
            data-testid="demo-search"
          />
        </div>

        <Select value={status || ALL} onValueChange={(v) => setStatus(v === ALL ? '' : v)}>
          <SelectTrigger className="w-[170px]" data-testid="demo-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{'All statuses'}</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          className="gap-1"
          disabled={syncing || items.length === 0}
          onClick={() => {
            void refreshUsage();
          }}
          data-testid="demo-refresh-usage"
          title="Check Shopify for redemptions of the codes on this page"
        >
          <RefreshCw className={`h-4 w-4${syncing ? ' animate-spin' : ''}`} />
          {syncing ? 'Refreshing…' : 'Refresh'}
        </Button>
      </div>

      <div className="flex flex-col gap-2">
        {error ? (
          <div
            className="rounded-md border border-destructive p-4 text-sm text-destructive"
            data-testid="demo-coupons-error"
          >
            {error.message}
          </div>
        ) : (
          <>
            <div className="shrink-0 text-xs text-muted-foreground" data-testid="demo-count">
              {loading && !page
                ? 'Loading…'
                : total > items.length
                  ? `${firstShown}–${lastShown} of ${total} coupons`
                  : `${total} coupon${total === 1 ? '' : 's'}`}
            </div>
            {/* Horizontal only: eleven columns overflow a narrow window, and that scroll belongs
                to the table. Vertical is the page's job now — a second vertical scroller nested
                in a scrolling page is the thing that traps a wheel gesture in the wrong box. */}
            <div className="overflow-x-auto rounded-md border">
              <DemoCouponTable coupons={items} />
            </div>
            {total > DEMO_PAGE_SIZE && (
              <div className="flex shrink-0 items-center justify-end gap-2 pt-1">
                <span className="text-xs text-muted-foreground" data-testid="demo-page-label">
                  {`Page ${pageIndex + 1} of ${Math.max(1, Math.ceil(total / DEMO_PAGE_SIZE))}`}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageIndex === 0 || loading}
                  onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                  data-testid="demo-page-prev"
                >
                  {'Previous'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={lastShown >= total || loading}
                  onClick={() => setPageIndex((p) => p + 1)}
                  data-testid="demo-page-next"
                >
                  {'Next'}
                </Button>
              </div>
            )}
            {/* `listed` — the code is in the table below, so the dialog must not claim it is the
                only copy. See MintedCouponDialog. */}
            <MintedCouponDialog minted={minted} onClose={() => setMinted(null)} listed />
          </>
        )}
      </div>
    </div>
  );
}
